<div class="gis-ops" [attr.data-mode]="mode">
  <div class="gis-ops-banner" *ngIf="!numericProjectId">
    Open a project to load employees, WBS, survey, attendance, machines, and materials.
  </div>

  <div class="gis-ops-banner" *ngIf="numericProjectId && loading">Loading project operations…</div>
  <div class="gis-ops-banner gis-ops-banner--error" *ngIf="loadError">{{ loadError }}</div>

  <ng-container *ngIf="numericProjectId && !loading">
    <div class="sidebar-section" *ngIf="mode === 'full'">
      <h3>Project parameters</h3>
      <div class="project-info">
        <div><strong>{{ projectName }}</strong></div>
        <div *ngIf="projectData?.stage">Stage: {{ prettyStatus(projectData.stage) }}</div>
        <div *ngIf="roadLength">Length: {{ roadLength }}</div>
        <div *ngIf="roadWidth">Width: {{ roadWidth }}</div>
        <div *ngIf="contractValue != null">Contract: {{ contractValue | currency:'INR':'symbol':'1.0-0' }}</div>
        <div *ngIf="projectData?.start_date">Start: {{ projectData.start_date | date:'dd/MM/yyyy' }}</div>
        <div *ngIf="projectData?.end_date">End: {{ projectData.end_date | date:'dd/MM/yyyy' }}</div>
        <div *ngIf="completionPct != null">Completion: {{ completionPct }}%</div>
        <div *ngIf="!roadLength && !roadWidth && contractValue == null && !projectData?.start_date"
             class="gis-ops-empty">No saved project parameters yet.</div>
      </div>
      <button type="button" class="btn" (click)="showProjectOnMap()">Show on map</button>
    </div>

    <div class="sidebar-section" *ngIf="mode === 'full'">
      <h3>Employees</h3>
      <div class="gis-ops-count">{{ staff.length }} allocated</div>
      <div class="gis-ops-list" *ngIf="staffPreview.length; else noStaff">
        <div class="gis-ops-row" *ngFor="let row of staffPreview">
          <div class="gis-ops-title">{{ staffName(row) }}</div>
          <div class="gis-ops-meta" *ngIf="staffMeta(row)">{{ staffMeta(row) }}</div>
        </div>
      </div>
      <ng-template #noStaff>
        <div class="gis-ops-empty">No staff allocated to this project.</div>
      </ng-template>
      <div class="btn-group">
        <button type="button" class="btn" (click)="showStaffOnMap()">Show on map</button>
        <a class="btn" [routerLink]="['/home/view-project', numericProjectId]">Open staff</a>
      </div>
    </div>

    <div class="sidebar-section" *ngIf="mode === 'full'">
      <h3>WBS</h3>
      <div class="gis-ops-count" *ngIf="wbs.length">{{ wbs.length }} activities · {{ wbsAverage }}% avg</div>
      <div class="gis-ops-list" *ngIf="wbsPreview.length; else noWbs">
        <div class="gis-ops-row" *ngFor="let row of wbsPreview">
          <div class="gis-ops-title">{{ wbsTitle(row) }}</div>
          <div class="gis-ops-meta">
            {{ row.percent_complete || 0 }}%
            <span *ngIf="row.assigned_to"> · {{ row.assigned_to }}</span>
          </div>
        </div>
      </div>
      <ng-template #noWbs>
        <div class="gis-ops-empty">No WBS activities for this project.</div>
      </ng-template>
      <a class="btn" [routerLink]="['/home/work-breakdown-structure', numericProjectId]">Open WBS</a>
    </div>

    <div class="sidebar-section" *ngIf="mode === 'full' || mode === 'survey'">
      <h3>Survey data</h3>
      <div class="gis-ops-count">{{ surveys.length }} records</div>
      <div class="gis-ops-list" *ngIf="surveyPreview.length; else noSurvey">
        <button type="button" class="gis-ops-row gis-ops-row--btn" *ngFor="let row of surveyPreview" (click)="showSurveyOnMap(row)">
          <div class="gis-ops-title">{{ surveyTitle(row) }}</div>
          <div class="gis-ops-meta">{{ surveyMeta(row) || 'No map link yet' }}</div>
        </button>
      </div>
      <ng-template #noSurvey>
        <div class="gis-ops-empty">No survey stages for this project.</div>
      </ng-template>
      <div class="btn-group">
        <button type="button" class="btn" (click)="showSurveyOnMap()">Show on map</button>
        <a class="btn" [routerLink]="['/home/survey-list']" [queryParams]="{ projectId: numericProjectId }">Open surveys</a>
      </div>
    </div>

    <div class="sidebar-section" *ngIf="mode === 'full' || mode === 'attendance'">
      <h3>{{ mode === 'attendance' ? 'Biometric attendance' : 'Attendance' }}</h3>
      <div class="gis-ops-count" *ngIf="todayAttendance.length">
        Today: Present {{ presentCount }}
        <span *ngIf="lateCount"> · Late {{ lateCount }}</span>
        · Absent {{ absentCount }}
      </div>
      <div class="gis-ops-count" *ngIf="!todayAttendance.length && attendance.length">
        Last 14 days: {{ attendance.length }} punches
      </div>
      <div class="gis-ops-count" *ngIf="geoPunchCount">{{ geoPunchCount }} with GPS</div>
      <div class="gis-ops-list" *ngIf="attendanceRows.length; else noAttendance">
        <div class="gis-ops-row" *ngFor="let row of attendanceRows">
          <div class="gis-ops-title" [attr.data-kind]="attendanceKind(row)">{{ attendanceName(row) }}</div>
          <div class="gis-ops-meta">
            {{ prettyStatus(row.status) || 'Present' }}
            <span *ngIf="row.intime"> · {{ row.intime }}</span>
          </div>
        </div>
      </div>
      <ng-template #noAttendance>
        <div class="gis-ops-empty">No attendance recorded for this project.</div>
      </ng-template>
      <div class="btn-group">
        <button type="button" class="btn" (click)="showAttendanceOnMap()">Map view</button>
        <a class="btn" [routerLink]="['/home/attendance']">View all</a>
      </div>
    </div>

    <div class="sidebar-section" *ngIf="mode === 'full'">
      <h3>Machines</h3>
      <div class="gis-ops-count">{{ machines.length }} reservations</div>
      <div class="gis-ops-list" *ngIf="machinePreview.length; else noMachines">
        <div class="gis-ops-row" *ngFor="let row of machinePreview">
          <div class="gis-ops-title">{{ machineTitle(row) }}</div>
          <div class="gis-ops-meta">{{ machineMeta(row) }}</div>
        </div>
      </div>
      <ng-template #noMachines>
        <div class="gis-ops-empty">No machines reserved on this project.</div>
      </ng-template>
      <div class="btn-group">
        <button type="button" class="btn" (click)="showMachinesOnMap()">Show on map</button>
        <a class="btn" [routerLink]="['/home/machine-reservations']" [queryParams]="{ project_id: numericProjectId }">Open machines</a>
      </div>
    </div>

    <div class="sidebar-section" *ngIf="mode === 'full'">
      <h3>Materials</h3>
      <div class="gis-ops-count" *ngIf="bomCode">BOM {{ bomCode }}</div>
      <div class="gis-ops-count">{{ materials.length }} line items</div>
      <div class="gis-ops-list" *ngIf="materialPreview.length; else noMaterials">
        <div class="gis-ops-row" *ngFor="let row of materialPreview">
          <div class="gis-ops-title">{{ materialTitle(row) }}</div>
          <div class="gis-ops-meta">{{ materialMeta(row) }}</div>
        </div>
      </div>
      <ng-template #noMaterials>
        <div class="gis-ops-empty">No project BOM materials yet.</div>
      </ng-template>
      <div class="btn-group">
        <button type="button" class="btn" (click)="showMaterialsOnMap()">Show on map</button>
        <a class="btn" [routerLink]="['/home/bom']">Open BOM</a>
      </div>
    </div>
  </ng-container>
</div>
